import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { EntryMode } from "./useEntryTypes";

export interface CommandEntry {
  id: string;
  restaurant_id: string;
  command_id: string | null;
  command_number: string;
  entry_type_id: string | null;
  entry_type_name: string;
  amount: number;
  entry_mode: EntryMode;
  opened_by: string;
  cash_register_id: string | null;
  order_id: string | null;
  status: string;
  consumed_amount: number;
  closed_at: string | null;
  created_at: string;
}

export interface CommandEntryReport {
  totalEntries: number;
  totalAmount: number;
  byType: { name: string; count: number; amount: number }[];
  byDate: { date: string; count: number; amount: number }[];
}

export function useCommandEntries() {
  const { activeRestaurant } = useRestaurantContext();
  const { user } = useAuth();
  const [entries, setEntries] = useState<CommandEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEntries = async (dateFrom?: string, dateTo?: string) => {
    if (!activeRestaurant?.id) return;

    try {
      let query = supabase
        .from("command_entries")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: false });

      if (dateFrom) {
        query = query.gte("created_at", dateFrom);
      }
      if (dateTo) {
        query = query.lte("created_at", dateTo + "T23:59:59");
      }

      const { data, error } = await query;

      if (error) throw error;

      const typedData: CommandEntry[] = (data || []).map(item => ({
        ...item,
        entry_mode: item.entry_mode as EntryMode
      }));

      setEntries(typedData);
    } catch (error) {
      console.error("Error fetching command entries:", error);
      toast.error("Erro ao carregar entradas");
    } finally {
      setIsLoading(false);
    }
  };

  const openCommands = async (
    commandNumbers: string[],
    entryType: {
      id: string;
      name: string;
      amount: number;
      entry_mode: EntryMode;
    },
    cashRegisterId?: string
  ) => {
    if (!activeRestaurant?.id || !user?.id) {
      toast.error("Erro: restaurante ou usuário não identificado");
      return null;
    }

    try {
      // Get command IDs for the selected numbers
      const { data: commands } = await supabase
        .from("commands")
        .select("id, number")
        .eq("restaurant_id", activeRestaurant.id)
        .in("number", commandNumbers);

      const commandMap = new Map(commands?.map((c) => [c.number, c.id]) || []);

      const entriesToInsert = commandNumbers.map((num) => ({
        restaurant_id: activeRestaurant.id,
        command_id: commandMap.get(num) || null,
        command_number: num,
        entry_type_id: entryType.id,
        entry_type_name: entryType.name,
        amount: entryType.amount,
        entry_mode: entryType.entry_mode,
        opened_by: user.id,
        cash_register_id: cashRegisterId || null,
        status: "active",
      }));

      const { data, error } = await supabase
        .from("command_entries")
        .insert(entriesToInsert)
        .select();

      if (error) throw error;

      // Update command status to occupied
      if (commands && commands.length > 0) {
        const commandIds = commands.map((c) => c.id);
        await supabase
          .from("commands")
          .update({ status: "occupied" })
          .in("id", commandIds);
      }

      const typedData: CommandEntry[] = (data || []).map(item => ({
        ...item,
        entry_mode: item.entry_mode as EntryMode
      }));

      setEntries((prev) => [...typedData, ...prev]);
      toast.success(`${commandNumbers.length} comanda(s) aberta(s) com sucesso!`);
      return typedData;
    } catch (error) {
      console.error("Error opening commands:", error);
      toast.error("Erro ao abrir comandas");
      return null;
    }
  };

  const closeEntry = async (entryId: string, consumedAmount?: number) => {
    try {
      const { error } = await supabase
        .from("command_entries")
        .update({
          status: "closed",
          consumed_amount: consumedAmount || 0,
          closed_at: new Date().toISOString(),
        })
        .eq("id", entryId);

      if (error) throw error;

      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                status: "closed",
                consumed_amount: consumedAmount || 0,
                closed_at: new Date().toISOString(),
              }
            : e
        )
      );
      return true;
    } catch (error) {
      console.error("Error closing entry:", error);
      toast.error("Erro ao fechar entrada");
      return false;
    }
  };

  const cancelEntry = async (entryId: string, reason?: string) => {
    try {
      // Get entry data to find the command
      const entry = entries.find((e) => e.id === entryId);
      if (!entry) {
        toast.error("Entrada não encontrada");
        return false;
      }

      // Update entry status to cancelled
      const { error: entryError } = await supabase
        .from("command_entries")
        .update({ 
          status: "cancelled",
          closed_at: new Date().toISOString(),
        })
        .eq("id", entryId);

      if (entryError) throw entryError;

      // Release the command (set status back to available)
      if (entry.command_id) {
        await supabase
          .from("commands")
          .update({ status: "available" })
          .eq("id", entry.command_id);
      }

      // Update local state
      setEntries((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, status: "cancelled", closed_at: new Date().toISOString() } : e))
      );

      toast.success(`Entrada da comanda ${entry.command_number} cancelada e estornada`);
      return { success: true, entry };
    } catch (error) {
      console.error("Error cancelling entry:", error);
      toast.error("Erro ao cancelar entrada");
      return { success: false };
    }
  };

  const updateEntry = async (
    entryId: string,
    newEntryType: {
      id: string;
      name: string;
      amount: number;
      entry_mode: EntryMode;
    },
    reason?: string
  ) => {
    try {
      const entry = entries.find((e) => e.id === entryId);
      if (!entry) {
        toast.error("Entrada não encontrada");
        return { success: false };
      }

      const oldAmount = entry.amount;
      const oldTypeName = entry.entry_type_name;
      const difference = newEntryType.amount - oldAmount;

      const { error } = await supabase
        .from("command_entries")
        .update({
          entry_type_id: newEntryType.id,
          entry_type_name: newEntryType.name,
          amount: newEntryType.amount,
          entry_mode: newEntryType.entry_mode,
        })
        .eq("id", entryId);

      if (error) throw error;

      // Update local state
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                entry_type_id: newEntryType.id,
                entry_type_name: newEntryType.name,
                amount: newEntryType.amount,
                entry_mode: newEntryType.entry_mode,
              }
            : e
        )
      );

      const differenceText = difference > 0 
        ? `Cliente paga +R$ ${Math.abs(difference).toFixed(2)}`
        : difference < 0
        ? `Devolver R$ ${Math.abs(difference).toFixed(2)} ao cliente`
        : "Sem diferença de valor";

      toast.success(`Entrada alterada: ${oldTypeName} → ${newEntryType.name}. ${differenceText}`);
      
      return { 
        success: true, 
        entry,
        oldAmount,
        oldTypeName,
        newAmount: newEntryType.amount,
        newTypeName: newEntryType.name,
        difference 
      };
    } catch (error) {
      console.error("Error updating entry:", error);
      toast.error("Erro ao alterar entrada");
      return { success: false };
    }
  };

  const getReport = (dateFrom?: string, dateTo?: string): CommandEntryReport => {
    let filteredEntries = entries.filter((e) => e.status !== "cancelled");

    if (dateFrom) {
      filteredEntries = filteredEntries.filter(
        (e) => e.created_at >= dateFrom
      );
    }
    if (dateTo) {
      filteredEntries = filteredEntries.filter(
        (e) => e.created_at <= dateTo + "T23:59:59"
      );
    }

    const totalEntries = filteredEntries.length;
    const totalAmount = filteredEntries.reduce((sum, e) => sum + e.amount, 0);

    // Group by type
    const byTypeMap = new Map<string, { count: number; amount: number }>();
    filteredEntries.forEach((e) => {
      const existing = byTypeMap.get(e.entry_type_name) || { count: 0, amount: 0 };
      byTypeMap.set(e.entry_type_name, {
        count: existing.count + 1,
        amount: existing.amount + e.amount,
      });
    });
    const byType = Array.from(byTypeMap.entries()).map(([name, data]) => ({
      name,
      ...data,
    }));

    // Group by date
    const byDateMap = new Map<string, { count: number; amount: number }>();
    filteredEntries.forEach((e) => {
      const date = e.created_at.split("T")[0];
      const existing = byDateMap.get(date) || { count: 0, amount: 0 };
      byDateMap.set(date, {
        count: existing.count + 1,
        amount: existing.amount + e.amount,
      });
    });
    const byDate = Array.from(byDateMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return { totalEntries, totalAmount, byType, byDate };
  };

  const getActiveEntries = () => {
    return entries.filter((e) => e.status === "active");
  };

  useEffect(() => {
    fetchEntries();
  }, [activeRestaurant?.id]);

  return {
    entries,
    isLoading,
    openCommands,
    closeEntry,
    cancelEntry,
    updateEntry,
    getReport,
    getActiveEntries,
    refetch: fetchEntries,
  };
}
